/**
 * The four states a signal can be found in, said in words (spec 090 FR-003).
 *
 * The service's own spellings are the keys — `SignalStatus` in
 * `app/features/triggers/models.py` — because renaming a backend entity in
 * frontend code introduces a translation tax that compounds (constitution §3).
 * Only the *label* is this feature's.
 *
 * Extracted from the component rather than inlined in JSX because it is the
 * derived value two obligations rest on (constitution §2.3, covered by tests):
 *
 * - **A word, always.** Colour or weight may accompany the state and may never
 *   replace it. Three of these four states are an ordered progression and the
 *   fourth is a failure, which is precisely what cannot be ranked by hue.
 * - **Unfinished is not an outcome.** A signal can sit in `in_progress`
 *   indefinitely if the worker processing it died. It is neither a success nor a
 *   failure, and presenting it as either — including in a count — states
 *   something the service never said.
 */
export interface SignalState {
  /** What the reader is told. */
  label: string;
  /** Whether processing has finished, either way. */
  terminal: boolean;
}

const STATES: Record<string, SignalState> = {
  pending: { label: 'Awaiting processing', terminal: false },
  in_progress: { label: 'Being processed', terminal: false },
  done: { label: 'Processed', terminal: true },
  failed: { label: 'Failed', terminal: true },
};

/**
 * The state a stored status names.
 *
 * A status this build does not recognise is reported as itself rather than as one
 * of the four — the service may grow a fifth, and claiming it is "processed"
 * because that is the nearest match would be a confident lie on a debugging
 * surface. It is treated as unfinished, which is the honest reading of a state
 * whose finality this build cannot know.
 */
export function signalState(status: string): SignalState {
  return STATES[status] ?? { label: status, terminal: false };
}

/** The statuses the filter offers, in the order processing moves through them. */
export const SIGNAL_STATUSES = [
  'pending',
  'in_progress',
  'done',
  'failed',
] as const;
